// src/features/works/components/DocumentsSection.jsx - CON RESTRICCIONES PARA FINALIZADAS
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Modal, Row, Col, Alert } from 'react-bootstrap';
import { FaPlus, FaFileAlt, FaClock, FaLock } from 'react-icons/fa';
import dayjs from 'dayjs';

// Componentes
import TableWrapper from '../../../components/TableWrapper';
import FilterSelect from '../../../components/FilterSelect';
import DocumentForm from './DocumentForm';

// Hooks
import { useColumnFilter } from '../../../hooks/useColumnFilter';

// Servicios
import WorksService from '../WorksService';

// Constantes
import { FORMATO_FECHA } from '../../../utils/constants';

/**
 * Sección para gestionar los documentos de una obra - CON RESTRICCIONES PARA FINALIZADAS
 */
const DocumentsSection = ({ workId, documents = [], onDataChange, showSnackbar, isWorkFinalized = false }) => {
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [filteredDocuments, setFilteredDocuments] = useState(documents);

  // Value getter para estado de vencimiento
  const expirationStatusGetter = useCallback((doc) => {
    if (!doc.endDate) return 'Sin fecha';
    
    const today = dayjs();
    const endDate = dayjs(doc.endDate);
    const daysUntilExpiration = endDate.diff(today, 'day');
    
    if (daysUntilExpiration < 0) return 'Vencido';
    if (daysUntilExpiration <= 30) return 'Por vencer';
    return 'Vigente';
  }, []);

  // Configuración de filtros
  const { 
    filterOptions: nameOptions, 
    activeFilters: activeNames,
    handleOptionsUpdate: handleNameOptionsUpdate,
    handleFiltersChange: handleNameChange,
    filterData: filterByName 
  } = useColumnFilter(documents, 'name', null, `work-${workId}-documents`);

  const { 
    filterOptions: statusOptions, 
    activeFilters: activeStatuses,
    handleOptionsUpdate: handleStatusOptionsUpdate,
    handleFiltersChange: handleStatusChange,
    filterData: filterByStatus 
  } = useColumnFilter(documents, 'endDate', expirationStatusGetter, `work-${workId}-documents`);

  // Actualizar documentos filtrados cuando cambian los datos o filtros
  useEffect(() => {
    let result = [...documents];
    
    // Aplicar filtros en cadena
    if (activeNames && activeNames.length > 0) {
      result = filterByName(result);
    }
    
    if (activeStatuses && activeStatuses.length > 0) {
      result = filterByStatus(result);
    }
    
    setFilteredDocuments(result);
  }, [documents, activeNames, activeStatuses, filterByName, filterByStatus]);

  // Parámetros para filtros de texto
  const textFilterParams = useMemo(() => ({
    debounceMs: 200,
    suppressAndOrCondition: true
  }), []);

  // Columnas para la tabla de documentos
  const documentColumns = useMemo(() => [
    { 
      headerName: 'Nombre', 
      field: 'name', 
      flex: 1.5,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    },
    { 
      headerName: 'Vencimiento', 
      field: 'endDate', 
      width: 140,
      valueFormatter: params => params.value ? dayjs(params.value).format(FORMATO_FECHA) : '',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterValueGetter: params => {
        return params.data.endDate ? dayjs(params.data.endDate).format(FORMATO_FECHA) : '';
      },
      filterParams: textFilterParams
    },
    {
      headerName: 'Estado',
      field: 'endDate',
      width: 100,
      valueGetter: params => expirationStatusGetter(params.data),
      cellRenderer: params => {
        const status = expirationStatusGetter(params.data);
        return `${status}`;
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true
    },
    { 
      headerName: 'Observaciones', 
      field: 'observations', 
      flex: 2,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: textFilterParams
    }
  ], [textFilterParams, expirationStatusGetter]);

  // Función para ver documento en nueva pestaña
  const handleViewDocument = async (doc) => {
    try {
      // Usar el servicio existente pero configurado para visualización
      const baseUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('access_token');
      
      if (!baseUrl || !token) {
        throw new Error('Configuración de autenticación no disponible');
      }
      
      const documentUrl = `${baseUrl}/documents/${doc.id}`;
      
      // Crear una nueva ventana
      const newWindow = window.open('', '_blank');
      if (!newWindow) {
        // Si no se puede abrir ventana, intentar descarga como fallback
        await handleDownloadDocument(doc);
        return;
      }
      
      // Mostrar contenido de carga
      newWindow.document.write(`
        <html>
          <head>
            <title>Cargando documento...</title>
            <style>
              body { 
                display: flex; 
                justify-content: center; 
                align-items: center; 
                height: 100vh; 
                font-family: Arial, sans-serif;
                margin: 0;
                background-color: #f5f5f5;
              }
              .loading {
                text-align: center;
                padding: 20px;
                background: white;
                border-radius: 8px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              }
            </style>
          </head>
          <body>
            <div class="loading">
              <div>Cargando documento...</div>
              <div style="margin-top: 10px; font-size: 14px; color: #666;">${doc.name}</div>
            </div>
          </body>
        </html>
      `);
      
      try {
        // Hacer fetch del documento con autenticación
        const response = await fetch(documentUrl, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        // Redirigir la ventana al documento
        newWindow.location.href = blobUrl;
        
        // Limpiar URL después de un tiempo
        setTimeout(() => {
          URL.revokeObjectURL(blobUrl);
        }, 10000);
        
        if (showSnackbar) {
          showSnackbar('success', 'Documento abierto en nueva pestaña');
        }
        
      } catch (fetchError) {
        // Si hay error en el fetch, mostrar error en la ventana
        newWindow.document.write(`
          <html>
            <head>
              <title>Error al cargar documento</title>
              <style>
                body { 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  height: 100vh; 
                  font-family: Arial, sans-serif;
                  margin: 0;
                  background-color: #f5f5f5;
                }
                .error {
                  text-align: center;
                  padding: 20px;
                  background: #fff;
                  border-radius: 8px;
                  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                  border-left: 4px solid #dc3545;
                }
              </style>
            </head>
            <body>
              <div class="error">
                <h3>Error al cargar el documento</h3>
                <p>${fetchError.message}</p>
                <p style="font-size: 14px; color: #666;">
                  Puede cerrar esta ventana e intentar descargar el documento.
                </p>
              </div>
            </body>
          </html>
        `);
        
        if (showSnackbar) {
          showSnackbar('error', `Error al abrir documento: ${fetchError.message}`);
        }
      }
      
    } catch (error) {
      console.error('Error al abrir documento:', error);
      if (showSnackbar) {
        showSnackbar('error', `Error al procesar el documento: ${error.message}`);
      }
    }
  };

  // Función para descargar documento
  const handleDownloadDocument = async (doc) => {
    try {
      const result = await WorksService.getDocument(doc.id, doc.name);
      
      if (result && result.success) {
        if (showSnackbar) {
          showSnackbar('success', 'Documento descargado correctamente');
        }
      } else {
        throw new Error(result?.error || 'Error desconocido al descargar el documento');
      }
    } catch (error) {
      console.error('Error al descargar documento:', error);
      if (showSnackbar) {
        showSnackbar('error', `Error al descargar el documento: ${error.message}`);
      }
    }
  };

  // ✅ FUNCIÓN PARA ELIMINAR DOCUMENTO - CON RESTRICCIÓN
  const handleDeleteDocument = async (document) => {
    if (isWorkFinalized) {
      if (showSnackbar) {
        showSnackbar('warning', 'No se pueden eliminar documentos de una obra finalizada');
      }
      return;
    }

    if (window.confirm(`¿Está seguro que desea eliminar el documento "${document.name}"?`)) {
      try {
        await WorksService.deleteDocument(document.id);
        
        if (showSnackbar) {
          showSnackbar('success', 'Documento eliminado correctamente');
        }
        
        if (onDataChange) {
          onDataChange();
        }
      } catch (error) {
        console.error('Error al eliminar documento:', error);
        if (showSnackbar) {
          showSnackbar('error', 'Error al eliminar el documento');
        }
      }
    }
  };

  // ✅ FUNCIÓN PARA NUEVO DOCUMENTO - CON RESTRICCIÓN
  const handleNewDocument = () => {
    if (isWorkFinalized) {
      if (showSnackbar) {
        showSnackbar('warning', 'No se pueden subir documentos a una obra finalizada');
      }
      return;
    }
    setSelectedDocument(null);
    setShowDocumentModal(true);
  };

  // ✅ FUNCIÓN PARA EDITAR DOCUMENTO - CON RESTRICCIÓN
  const handleEditDocument = (document) => {
    if (isWorkFinalized) {
      if (showSnackbar) {
        showSnackbar('warning', 'No se pueden editar documentos de una obra finalizada');
      }
      return;
    }
    setSelectedDocument(document);
    setShowDocumentModal(true);
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
  };

  const handleDocumentSaved = () => {
    setShowDocumentModal(false);
    setSelectedDocument(null);
    
    if (showSnackbar) {
      showSnackbar(
        'success', 
        selectedDocument ? 'Documento actualizado correctamente' : 'Documento subido correctamente'
      );
    }
    
    if (onDataChange) {
      onDataChange();
    }
  };

  return (
    <div className="d-flex flex-column h-100">
      {/* ✅ ALERTA PARA OBRAS FINALIZADAS */}
      {isWorkFinalized && (
        <Alert variant="info" className="mb-3 d-flex align-items-center">
          <FaLock className="me-2" />
          <small>
            Los documentos de esta obra finalizada están en modo de solo lectura.
            Puede visualizar y descargar documentos, pero no realizar modificaciones.
          </small>
        </Alert>
      )}

      {/* Fila de filtros y botón */}
      <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            {/* Filtros dropdown */}
            <div className="d-flex gap-2">
              <FilterSelect
                options={nameOptions}
                activeFilters={activeNames}
                onFiltersChange={handleNameChange}
                onOptionsUpdate={handleNameOptionsUpdate}
                title="Filtrar por documento"
                filterKey={`work-${workId}-documents-names`}
                icon={<FaFileAlt />}
                variant="outline-secondary"
              />
              
              <FilterSelect
                options={statusOptions}
                activeFilters={activeStatuses}
                onFiltersChange={handleStatusChange}
                onOptionsUpdate={handleStatusOptionsUpdate}
                title="Filtrar por estado"
                filterKey={`work-${workId}-documents-status`}
                icon={<FaClock />}
                variant="outline-secondary"
              />
            </div>
            
            {/* ✅ BOTÓN CONDICIONAL PARA SUBIR DOCUMENTO */}
            {isWorkFinalized ? (
              <Button 
                variant="secondary"
                size="sm"
                className="d-flex align-items-center"
                disabled
              >
                <FaLock className="me-1" /> Obra Finalizada
              </Button>
            ) : (
              <Button 
                variant="primary"
                size="sm"
                className="d-flex align-items-center"
                onClick={handleNewDocument}
              >
                <FaPlus className="me-1" /> Subir Documento
              </Button>
            )}
          </div>
        </Col>
      </Row>
      
      {/* Tabla */}
      <div className="flex-grow-1" style={{ height: 'calc(100vh - 280px)' }}>
        <TableWrapper
          columnDefs={documentColumns}
          rowData={filteredDocuments}
          tableType="documentos"
          onView={handleViewDocument}
          onEdit={isWorkFinalized ? null : handleEditDocument} // ✅ DESHABILITAR EDICIÓN
          onDelete={isWorkFinalized ? null : handleDeleteDocument} // ✅ DESHABILITAR ELIMINACIÓN
          onDownload={handleDownloadDocument}
          customGridOptions={{
            pagination: true,
            paginationPageSize: 20,
            domLayout: 'autoHeight'
          }}
        />
      </div>
      
      {/* Modal para subir/editar documento */}
      <Modal 
        show={showDocumentModal} 
        onHide={handleCloseDocumentModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className='text-center w-100'>
            {selectedDocument ? 'Editar Documento' : 'Subir Nuevo Documento'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showDocumentModal && (
            <DocumentForm 
              workId={workId}
              document={selectedDocument}
              onSave={handleDocumentSaved}
              onCancel={handleCloseDocumentModal}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DocumentsSection;